import React, { useState, useEffect, useMemo } from 'react';
import { Modal, Form, Select, Input, Radio, Checkbox, Button, message, Tooltip, Spin } from 'antd';
import { CloseOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import AIRichText from 'components/AIRichText';
import { EAIRichTextEditorType } from 'types/ai';
import s from './s.module.less';
import useGetTemplateDetail from 'hooks/videoSetting/useGetTemplateDetail';
import { createTreatmentTemplate, updateTreatmentTemplate } from 'api/videoSetting';
import { autoSaveInfo } from 'api/treatment';
import { EAutoSaveBusinessType } from 'types/treatment';
import useProviderKlarityServiceList from 'hooks/useProviderKlarityServiceList';

interface TreatmentPlanModalProps {
    open: boolean;
    onCancel: () => void;
    onSuccess: () => void;
    templateId?: number; // 编辑模式时传入
    specialties?: { label: string; key: string }[];
    specialty?: string; // 特殊情况可以指定 specialty 和 visitType，不能修改
    visitType?: string;
    isStandalone?: boolean;
}

const VisitTypeMap = {
    'New patient initial visit': 'Initial visit',
    'Established patient visit': 'Follow-up visit',
    PRESCRIPTION_REFILL: 'Prescription refill',
};

const TreatmentPlanModal: React.FC<TreatmentPlanModalProps> = ({
    open,
    onCancel,
    onSuccess,
    templateId,
    specialties = [],
    specialty,
    visitType,
    isStandalone,
}) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [content, setContent] = useState<string>('');

    const needFetchSpecialties = !!isStandalone && specialties.length === 0;
    const [providerServiceList] = useProviderKlarityServiceList(false, undefined, !needFetchSpecialties);

    const resolvedSpecialties = useMemo(() => {
        if (specialties.length > 0) return specialties;
        if (!providerServiceList) return [];
        const seen = new Set<string>();
        const result: { label: string; key: string }[] = [];
        providerServiceList.forEach((item) => {
            if (item.serviceSpecialtyName && !seen.has(item.serviceSpecialtyName)) {
                seen.add(item.serviceSpecialtyName);
                result.push({ label: item.serviceSpecialtyName, key: item.serviceSpecialtyName });
            }
        });
        return result;
    }, [specialties, providerServiceList]);

    const isEditMode = !!templateId;
    const [templateData, templateLoading, fetchTemplate] = useGetTemplateDetail(
        templateId,
        Boolean(open && isEditMode),
    );

    const specialtyValue = Form.useWatch('specialty', form);
    const visitTypeValue = Form.useWatch('serviceType', form);
    const templateName = Form.useWatch('name', form);
    const specialtyName = resolvedSpecialties.find((item) => item.key === specialtyValue)?.label || specialtyValue;
    const visitTypeName = VisitTypeMap[visitTypeValue as keyof typeof VisitTypeMap] || '';

    useEffect(() => {
        if (templateData) {
            setContent(templateData.content);
            form.setFieldsValue({
                specialty: templateData.specialty,
                name: templateData.name,
                serviceType: templateData.serviceType,
                isDefault: templateData.isDefault,
            });
        }
    }, [templateData, form]);

    useEffect(() => {
        if (open && !isEditMode) {
            if (specialty || visitType) {
                form.setFieldsValue({
                    ...(specialty ? { specialty } : {}),
                    ...(visitType ? { serviceType: visitType } : {}),
                });
            } else {
                form.resetFields();
                setContent('');
            }
        }
    }, [open, specialty, visitType, isEditMode, form]);

    const handleCancel = () => {
        form.resetFields();
        setContent('');
        onCancel();
    };

    const handleConfirm = async () => {
        try {
            const values = await form.validateFields();

            if (!content || content.trim() === '' || content === '<p><br></p>') {
                message.error('Please enter content');
                return;
            }

            setLoading(true);

            const submitData = {
                name: values.name,
                specialty: values.specialty,
                serviceType: values.serviceType,
                content,
                isDefault: values.isDefault || false,
            };

            if (isEditMode && templateId) {
                await updateTreatmentTemplate(templateId, submitData);
                message.success('Template updated successfully');

                // 若 name 或 content 发生变化，调用 autoSaveInfo 保存变更记录
                const nameChanged = templateData && templateData.name !== values.name;
                const contentChanged = templateData && templateData.content !== content;
                if (nameChanged || contentChanged) {
                    try {
                        await autoSaveInfo({
                            autoSaveBusinessType: EAutoSaveBusinessType.TREATMENT_PLAN_TEMPLATE_CONTENT,
                            businessId: templateId,
                            content: JSON.stringify({ name: values.name, content }),
                        });
                    } catch (err) {
                        console.error('Failed to auto-save template change:', err);
                    }
                }
            } else {
                await createTreatmentTemplate(submitData);
                message.success('Template created successfully');
            }
            onSuccess();
            handleCancel();
        } catch (error) {
            console.error('Failed to save template:', error);
            message.error('Failed to save template. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async () => {
        const values = await form.validateFields();

        if (!content || content.trim() === '' || content === '<p><br></p>') {
            message.error('Please enter content');
            return;
        }

        if (isEditMode) {
            if (!values.isDefault && templateData?.isDefault) {
                Modal.confirm({
                    title: 'Update and remove default',
                    content: `This update will save your changes, but the template will no longer be the default for ${visitTypeName} in ${specialtyName}. Future appointments of this type will no longer use this template.`,
                    onOk: handleConfirm,
                    okText: 'Update',
                    onCancel: () => { },
                    icon: null,
                    width: 500,
                    closable: true,
                    wrapClassName: s.customConfirmRoot,
                });
            } else {
                Modal.confirm({
                    title: 'Update template',
                    content:
                        'This update will apply to all future visits. Existing visit notes and drafts will not be affected.',
                    onOk: handleConfirm,
                    okText: 'Update',
                    onCancel: () => { },
                    icon: null,
                    width: 500,
                    okButtonProps: {
                        className: s.confirmButton,
                    },
                    closable: true,
                    wrapClassName: s.customConfirmRoot,
                });
            }
        } else if (values.isDefault) {
            Modal.confirm({
                title: 'Confirm default template ',
                content: `Are you sure you want to set ${templateName} as the default for ${visitTypeName} in ${specialtyName}? Any existing default template for this visit type will be replaced.`,
                okText: 'Confirm',
                onOk: handleConfirm,
                icon: null,
                onCancel: () => { },
                width: 500,
                okButtonProps: {
                    className: s.confirmButton,
                },
                closable: true,
                wrapClassName: s.customConfirmRoot,
            });
        } else {
            handleConfirm();
        }
    };

    const getDefaultCheckboxLabel = () => {
        if (!specialtyValue || !visitTypeValue) {
            return 'Set as default';
        }
        return `Set as default for ${visitTypeName} in ${specialtyName}`;
    };

    return (
        <Modal
            open={open}
            onCancel={handleCancel}
            footer={[
                <Button onClick={handleCancel} className={s.cancelButton}>
                    Cancel
                </Button>,
                <Button
                    type="primary"
                    onClick={handleSubmit}
                    className={s.submitButton}
                    loading={loading}
                    disabled={loading}
                >
                    {isEditMode ? 'Save' : 'Create'}
                </Button>,
            ]}
            width={800}
            className={s.modal}
            closeIcon={<CloseOutlined />}
            title={isEditMode ? 'Edit template' : 'Create template'}
            destroyOnClose
        >
            <Spin spinning={templateLoading}>
                <div className={s.content}>
                    {!isEditMode && (
                        <p className={s.description}>
                            Visit Notes templates help you save time by pre-filling patient-facing plans. You can always
                            edit the content during a visit.
                        </p>
                    )}

                    <Form form={form} layout="vertical">
                        <Form.Item
                            label="Specialty"
                            name="specialty"
                            rules={[{ required: !isStandalone, message: 'Please select a specialty' }]}
                        >
                            <Select
                                placeholder="Select a specialty"
                                className={s.select}
                                options={resolvedSpecialties.map((item) => ({ label: item.label, value: item.key }))}
                                disabled={!!specialty}
                                allowClear={!!isStandalone}
                            />
                        </Form.Item>

                        <Form.Item
                            label="Name"
                            name="name"
                            rules={[{ required: true, message: 'Please enter template name' }]}
                            help={
                                <span className={s.helpText}>
                                    For internal reference only. Patients will not see this name.
                                </span>
                            }
                        >
                            <Input className={s.input} />
                        </Form.Item>

                        <Form.Item
                            label="Visit type"
                            name="serviceType"
                            rules={[{ required: !isStandalone, message: 'Please select visit type' }]}
                        >
                            <Radio.Group className={s.radioGroup} disabled={!!visitType}>
                                <Radio value="New patient initial visit">Initial visit</Radio>
                                <Radio value="Established patient visit">Follow-up visit</Radio>
                                <Radio value="PRESCRIPTION_REFILL">Prescription refill</Radio>
                            </Radio.Group>
                        </Form.Item>

                        <Form.Item label="Content" required>
                            <div className={s.contentEditor}>
                                <AIRichText
                                    value={content}
                                    onChange={setContent}
                                    relationId={templateId || 0}
                                    type={EAIRichTextEditorType.TREATMENT_PLAN_TEMPLATE_GENERATE}
                                    placeholder=""
                                    height="200px"
                                    context={{
                                        specialty: specialtyValue,
                                        serviceType: visitTypeValue,
                                    }}
                                />
                            </div>
                        </Form.Item>

                        <Form.Item name="isDefault" valuePropName="checked">
                            <Checkbox className={s.checkbox}>
                                {getDefaultCheckboxLabel()}
                                <Tooltip title="This template will automatically load for all future visits of this type. You can still switch to a different template manually during the visit.">
                                    <QuestionCircleOutlined className={s.questionIcon} />
                                </Tooltip>
                            </Checkbox>
                        </Form.Item>
                    </Form>
                </div>
            </Spin>
        </Modal>
    );
};

export default TreatmentPlanModal;
